import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  ShoppingCart, Plus, Trash2, Pencil, Send, DollarSign,
  Package, TrendingUp, Loader2, ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WOO_CATEGORIES = [
  "Ogolne", "Elektronika", "Odziez", "Dom i Ogrod", "Zdrowie",
  "Sport", "Motoryzacja", "Ksiazki", "Zabawki", "Zywnosc"
];

export default function WooProducts() {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", short_description: "",
    regular_price: "", sale_price: "", cost_price: "",
    sku: "", category: "Ogolne", tags: "", image_url: "",
    status: "draft", stock_status: "instock", site_id: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [prodRes, statsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/woo/products`),
        axios.get(`${API}/woo/stats`),
        axios.get(`${API}/sites`),
      ]);
      setProducts(prodRes.data);
      setStats(statsRes.data);
      setSites(sitesRes.data.filter(s => s.site_type === "wordpress"));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ name: "", description: "", short_description: "",
      regular_price: "", sale_price: "", cost_price: "",
      sku: "", category: "Ogolne", tags: "", image_url: "",
      status: "draft", stock_status: "instock", site_id: "" });
    setEditing(null);
  };

  const calcMargin = () => {
    const rp = parseFloat(form.regular_price || "0");
    const cp = parseFloat(form.cost_price || "0");
    if (rp > 0 && cp > 0) return ((rp - cp) / rp * 100).toFixed(1);
    return null;
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map(t2 => t2.trim()).filter(Boolean) : [], site_id: form.site_id || null };
      if (editing) {
        await axios.put(`${API}/woo/products/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/woo/products`, payload);
      }
      toast.success(t("common.success"));
      fetchData(); setDialogOpen(false); resetForm();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || "", short_description: p.short_description || "",
      regular_price: p.regular_price || "", sale_price: p.sale_price || "", cost_price: p.cost_price || "",
      sku: p.sku || "", category: p.category, tags: (p.tags || []).join(", "), image_url: p.image_url || "",
      status: p.status, stock_status: p.stock_status || "instock", site_id: p.site_id || "" });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/woo/products/${id}`); toast.success(t("common.success")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
  };

  const handlePublish = async (id) => {
    setPublishing(id);
    try { await axios.post(`${API}/woo/products/${id}/publish`); toast.success(t("woo.published")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
    finally { setPublishing(null); }
  };

  const margin = calcMargin();

  return (
    <div data-testid="woo-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("woo.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("woo.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-woo-btn" className="rounded-lg gap-2">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("woo.addProduct")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? t("sites.edit") : t("woo.addProduct")}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3 py-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("woo.productName")}</Label>
                <Input data-testid="woo-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("woo.regularPrice")} (PLN)</Label>
                  <Input data-testid="woo-price" type="number" value={form.regular_price} onChange={(e) => setForm({ ...form, regular_price: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("woo.salePrice")}</Label>
                  <Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("woo.costPrice")}</Label>
                  <Input type="number" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} />
                </div>
              </div>
              {margin && (
                <div className={`p-2 rounded-lg text-center text-xs font-semibold ${parseFloat(margin) > 30 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/10 dark:text-emerald-400" : "bg-amber-50 text-amber-700 dark:bg-amber-900/10 dark:text-amber-400"}`}>
                  {t("woo.margin")}: {margin}%
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">{t("woo.shortDesc")}</Label>
                <Textarea value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} rows={2} className="resize-none text-xs" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("woo.category")}</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{WOO_CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">SKU</Label>
                  <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("woo.imageUrl")}</Label>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("woo.tags")}</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="tag1, tag2..." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("woo.stock")}</Label>
                  <Select value={form.stock_status} onValueChange={(v) => setForm({ ...form, stock_status: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instock" className="text-xs">{t("woo.inStock")}</SelectItem>
                      <SelectItem value="outofstock" className="text-xs">{t("woo.outOfStock")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {sites.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("woo.targetSite")}</Label>
                    <Select value={form.site_id || "none"} onValueChange={(v) => setForm({ ...form, site_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" className="text-xs">—</SelectItem>
                        {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>{t("sites.cancel")}</Button>
              <Button data-testid="save-woo-btn" onClick={handleSave}>{t("sites.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t("woo.totalProducts"), value: stats.total, icon: Package, color: "text-slate-500" },
            { label: t("woo.publishedProducts"), value: stats.published, icon: ShoppingCart, color: "text-emerald-500" },
            { label: t("woo.draftProducts"), value: stats.draft, icon: DollarSign, color: "text-amber-500" },
          ].map((s, i) => (
            <Card key={i} className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-4 flex items-center gap-3">
                <s.icon className={`w-5 h-5 ${s.color}`} strokeWidth={1.5} />
                <div>
                  <p className="text-xl font-bold font-heading text-foreground">{s.value}</p>
                  <p className="text-[10px] text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Table */}
      <Card className="rounded-xl border shadow-sm bg-card">
        <CardContent className="p-0">
          {products.length === 0 ? (
            <div data-testid="no-woo" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <ShoppingCart className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
              <p className="text-xs">{t("woo.noProducts")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold">{t("woo.productName")}</TableHead>
                  <TableHead className="text-xs font-semibold">{t("woo.category")}</TableHead>
                  <TableHead className="text-xs font-semibold">{t("woo.regularPrice")}</TableHead>
                  <TableHead className="text-xs font-semibold">{t("woo.margin")}</TableHead>
                  <TableHead className="text-xs font-semibold">{t("woo.stock")}</TableHead>
                  <TableHead className="text-xs font-semibold">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-right">{t("sites.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`woo-row-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                        <span className="text-xs font-medium">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] rounded-md">{p.category}</Badge></TableCell>
                    <TableCell className="text-xs font-semibold">{p.regular_price} PLN</TableCell>
                    <TableCell>
                      {p.margin != null ? (
                        <span className={`text-xs font-semibold ${p.margin > 30 ? "text-emerald-600" : "text-amber-600"}`}>{p.margin}%</span>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] border-0 rounded-md ${p.stock_status === "instock" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"}`}>
                        {p.stock_status === "instock" ? t("woo.inStock") : t("woo.outOfStock")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] border-0 rounded-md ${p.published_to_woo ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500"}`}>
                        {p.published_to_woo ? t("woo.publishedProducts") : "Draft"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!p.published_to_woo && (
                          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 text-emerald-600"
                            data-testid={`publish-woo-${p.id}`}
                            onClick={() => handlePublish(p.id)} disabled={publishing === p.id}>
                            {publishing === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" strokeWidth={1.5} />}
                            {t("woo.publish")}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(p)}>
                          <Pencil className="w-3 h-3" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p.id)}>
                          <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
